import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  approveChatAction,
  chatActionQuery,
  chatActionsShellQuery,
  chatActionsTableQuery,
  quickChatAction,
  rejectChatAction,
  removeChatAction,
  APPROVED,
  REJECTED,
  type ChatActionsShell,
} from '../../api/settingsEaseedeskChatActions'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Recommended actions.
 *
 * Element cu element fata de `version-3/easeedesk_settings/chat/
 * recommended_actions/index.php`, si coloana cu coloana fata de
 * `chatRecommendedActionsModel` din `public/js/settings/easeedesk/
 * tickets_settings.js`.
 *
 * CE NU MAI E: fereastra "Suggest new action". Ea nu trimitea aici, ci in
 * chat (`chat_conversation/addNewActionSuggestion`), si nici catalogul de
 * comenzi nu-l cerea de la ecranul asta (`chat_conversation/
 * getCmdCommandsList`). Adica era o bucata din ALT modul, gazduita pe pagina
 * de setari. Ea se muta cu chatul; aici ar fi insemnat un al doilea drum catre
 * un controller nemutat, si un buton care pica.
 *
 * Clicul se prinde pe invelis, ca la Trigger actions: celulele sunt HTML pus
 * cu `innerHTML`, deci butoanele din ele n-au `onClick` propriu.
 */

const TABLE_ID = 'chat-actions'

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** Culoarea punctelor din lista, ca in modelul vechi. */
function dotClass(status: number): string {
  if (status === 0) return 'color-blue'
  if (status === APPROVED) return 'color-green2'
  return ''
}

function bullets(items: string[], status: number, icon: string): string {
  if (items.length === 0) return ''
  const cls = dotClass(status)
  return (
    '<ul>' +
    items.map((item) => `<li class="${cls}"><i class="${icon}" aria-hidden="true"></i> ${esc(item)}</li>`).join('') +
    '</ul>'
  )
}

export function chatActionColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: 'Service provider',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.service_provider_name ?? '') || 'General'),
    },
    {
      index: 1,
      title: 'Company',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.company_name ?? '') || 'All companies'),
    },
    {
      index: 2,
      title: 'Keywords',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.search_value ?? '')),
    },
    {
      index: 3,
      title: 'Messages',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) => bullets((row.messages as string[]) ?? [], Number(row.status), 'fas fa-circle'),
    },
    {
      index: 4,
      title: 'CMD',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) => bullets((row.commands as string[]) ?? [], Number(row.status), 'fas fa-check'),
    },
    {
      index: 5,
      title: 'Status',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => {
        const status = Number(row.status)
        if (status === APPROVED) return '<span class="label label-success">approved</span>'
        if (status === REJECTED) return '<span class="label label-danger">rejected</span>'
        return '<span class="label label-info">pending..</span>'
      },
    },
    {
      index: 6,
      title: 'Quick Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) => {
        const id = Number(row.ID)
        const status = Number(row.status)
        const approve =
          `<button type="button" data-quick="aprove" data-action="${id}" ` +
          'class="btn btn-xs btn-success quick-action-btn"><i class="fas fa-check" aria-hidden="true"></i> Aprove</button>'
        const reject =
          `<button type="button" data-quick="reject" data-action="${id}" ` +
          'class="btn btn-xs btn-danger quick-action-btn"><i class="fas fa-times-circle" aria-hidden="true"></i> Reject</button>'

        const inside = status === APPROVED ? reject : status === REJECTED ? approve : approve + reject

        return `<div class="btn-group" role="group">${inside}</div>`
      },
    },
    {
      index: 7,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) => {
        const id = Number(row.ID)
        return (
          '<div class="btn-group" role="group">' +
          `<button type="button" data-action="${id}" class="btn btn-xs btn-primary edit-action">` +
          '<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>' +
          `<button type="button" data-action="${id}" class="btn btn-xs btn-danger remove-action">` +
          '<i class="fas fa-times" aria-hidden="true"></i> Remove</button>' +
          '</div>'
        )
      },
    },
    {
      index: 8,
      title: 'User',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) => esc(String(row.user_name ?? '') || 'System'),
    },
  ]
}

/** Numele coloanei SQL pentru fiecare indice, ca in harta din model. */
export const ORDER_NAMES = [
  'it_service_providers.service_provider_name',
  'firma.Nume',
  'it_easeedesk_chat_recommended_actions.search_value',
  'it_easeedesk_chat_recommended_actions.status',
  'it_easeedesk_chat_recommended_actions.status',
  'it_easeedesk_chat_recommended_actions.status',
]

export function ChatRecommendedActionsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(chatActionsShellQuery())
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 3, dir: 'desc' },
    search: '',
  })
  const [edited, setEdited] = useState(0)
  const columns = useMemo(() => chatActionColumns(), [])

  const table = useQuery({
    ...chatActionsTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? ORDER_NAMES[3],
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'chat_recommended_actions'] })
  }

  const kill = useMutation({
    mutationFn: (id: number) => removeChatAction(id),
    onSuccess: () => {
      toast('Action removed.', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const quick = useMutation({
    mutationFn: (v: { type: 'aprove' | 'reject'; id: number }) => quickChatAction(v.type, v.id),
    onSuccess: () => {
      toast('Action saved.', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Recommended Actions" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const edit = target.closest('.edit-action')
    if (edit instanceof HTMLElement) {
      event.preventDefault()
      setEdited(Number(edit.dataset.action ?? 0))
      return
    }

    const remove = target.closest('.remove-action')
    if (remove instanceof HTMLElement) {
      event.preventDefault()
      if (!window.confirm('Are you sure?\nYou will not be able to recover this action!')) return
      kill.mutate(Number(remove.dataset.action ?? 0))
      return
    }

    const fast = target.closest('.quick-action-btn')
    if (fast instanceof HTMLElement) {
      event.preventDefault()
      const type = fast.dataset.quick === 'reject' ? 'reject' : 'aprove'
      quick.mutate({ type, id: Number(fast.dataset.action ?? 0) })
    }
  }

  return (
    <div onClick={onClick}>
      <h4>
        <strong>Recommended Actions</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Edit and approve recommended actions
      </label>
      <hr />

      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => `row_${Number(row.ID)}`}
        emptyHint="A recommended action answers a chat message with ready-made replies or with commands to run on the device."
      />

      <ActionModal
        id={edited}
        open={edited > 0}
        shell={shell.data}
        onClose={() => setEdited(0)}
        onSaved={() => {
          setEdited(0)
          redraw()
        }}
      />
    </div>
  )
}

/**
 * "Edit and approve action": pentru cine e regula, cuvintele cheie, mesajele
 * (cate randuri vrei) si comenzile.
 *
 * Mesajele se adaugau in portal clonand randul cu butonul verde; aici sunt un
 * sir de texte, cu acelasi gest si acelasi rezultat.
 */
function ActionModal({
  id,
  open,
  shell,
  onClose,
  onSaved,
}: {
  id: number
  open: boolean
  shell: ChatActionsShell
  onClose(): void
  onSaved(): void
}) {
  const details = useQuery({ ...chatActionQuery(id), enabled: open && id > 0 })

  const [provider, setProvider] = useState(0)
  const [company, setCompany] = useState(0)
  const [keywords, setKeywords] = useState('')
  const [messages, setMessages] = useState<string[]>([''])
  const [commands, setCommands] = useState<number[]>([])

  useEffect(() => {
    if (!details.data) return
    setProvider(details.data.details.service_provider ?? 0)
    setCompany(details.data.details.company_id ?? 0)
    setKeywords(details.data.details.search_value)
    setMessages(details.data.messages.length > 0 ? details.data.messages.map((m) => m.message) : [''])
    setCommands(details.data.commands.filter((c) => c.selected).map((c) => c.id))
  }, [details.data])

  const save = useMutation({
    mutationFn: () =>
      approveChatAction({
        action_id: id,
        service_provider: provider,
        company_id: company,
        keywords,
        messages: messages.map((m) => m.trim()).filter((m) => m !== ''),
        commands,
      }),
    onSuccess: () => {
      toast('Action saved.', 'Success')
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const reject = useMutation({
    mutationFn: () => rejectChatAction(id),
    onSuccess: () => {
      toast('Action rejected.', 'Success')
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="editChatActionModal"
      labelledBy="editChatActionModalLabel"
      open={open}
      onClose={onClose}
      title="Edit and approve action"
    >
      {details.error ? (
        /* Fereastra se deschide cu o cerere proprie. Cand ea pica, ramanea o
           rotita care se invartea in mijlocul ferestrei, si singura iesire era
           inchiderea ei. */
        <LoadFailed what="This action" error={details.error} onRetry={() => void details.refetch()} />
      ) : details.data ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <div className="form-group">
            <label className="control-label">Service provider</label>
            <select
              className="form-control input-sm"
              value={String(provider)}
              onChange={(e) => setProvider(Number(e.target.value))}
            >
              <option value="0">General</option>
              <option value={String(shell.service_provider)}>{shell.service_provider_name}</option>
            </select>
          </div>

          <div className="form-group">
            <label className="control-label">Company</label>
            <select
              className="form-control input-sm"
              value={String(company)}
              onChange={(e) => setCompany(Number(e.target.value))}
            >
              <option value="0">All companies</option>
              {shell.companies.map((c) => (
                <option key={c.ID} value={c.ID}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="control-label">Keywords</label>
            <input
              type="text"
              className="form-control"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="control-label">Messages</label>
            {messages.map((message, index) => (
              <div className="entry input-group" key={index} style={{ marginBottom: 4 }}>
                <input
                  type="text"
                  className="form-control"
                  value={message}
                  onChange={(e) =>
                    setMessages(messages.map((m, i) => (i === index ? e.target.value : m)))
                  }
                />
                <span className="input-group-btn">
                  <button
                    className="btn btn-success btn-add"
                    type="button"
                    onClick={() => setMessages([...messages, ''])}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                  <button
                    className="btn btn-danger btn-remove"
                    type="button"
                    onClick={() =>
                      setMessages(
                        messages.length === 1
                          ? ['']
                          : messages.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                </span>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label className="control-label">Cmd commands</label>
            {details.data.commands.length === 0 ? (
              <select className="form-control input-sm" disabled>
                <option value="0">No cmd commands found</option>
              </select>
            ) : (
              <select
                className="form-control input-sm"
                multiple
                size={Math.min(10, details.data.commands.length)}
                value={commands.map(String)}
                onChange={(e) =>
                  setCommands(Array.from(e.target.selectedOptions, (o) => Number(o.value)))
                }
              >
                {details.data.commands.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-success approve-action" disabled={save.isPending}>
              Approve
            </button>{' '}
            <button
              type="button"
              className="btn btn-danger reject-action"
              disabled={reject.isPending}
              onClick={() => reject.mutate()}
            >
              Reject
            </button>
          </div>
        </form>
      ) : (
        <Spinner />
      )}
    </Modal>
  )
}
